import { useEffect, useImperativeHandle, useRef, forwardRef } from "react";

/**
 * Touch-first canvas signature pad. Exposes clear() and toDataURL() via ref.
 */
const SignaturePad = forwardRef(function SignaturePad({ onChange }, ref) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const emptyRef = useRef(true);
  const lastRef = useRef({ x: 0, y: 0 });

  const resize = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    const dpr = window.devicePixelRatio || 1;
    const w = parent.clientWidth;
    const h = 256; // h-64
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2.6;
    ctx.strokeStyle = "#064e3b";
    ctx.fillStyle = "#fafaf9";
    ctx.fillRect(0, 0, w, h);
    emptyRef.current = true;
    onChange && onChange(true);
  };

  useEffect(() => {
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = getPos(e);
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const p = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
    if (emptyRef.current) {
      emptyRef.current = false;
      onChange && onChange(false);
    }
  };

  const end = () => {
    drawingRef.current = false;
  };

  useImperativeHandle(ref, () => ({
    clear: () => resize(),
    isEmpty: () => emptyRef.current,
    toDataURL: () => canvasRef.current.toDataURL("image/png"),
  }));

  return (
    <div className="w-full h-64 bg-stone-50 border-2 border-dashed border-stone-300 rounded-2xl overflow-hidden no-touch-scroll">
      <canvas
        ref={canvasRef}
        data-testid="signature-canvas"
        className="signature-canvas block w-full h-full"
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
      />
    </div>
  );
});

export default SignaturePad;
